import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Select,
  Typography,
  Button,
  Form,
  Input,
  Tag,
  Space,
} from "antd";
import {
  SaveOutlined,
  EditOutlined,
  RobotOutlined,
  TeamOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { notify } from "../../../../utils/helper";
import scriptService from '../../../../services/script';
import { physicianService } from '../../../../services/user';
import TextArea from 'antd/lib/input/TextArea';
import { BsStars } from 'react-icons/bs';
import GenereateScriptWithAi from './GenerateScriptWithAi';
import { CustomButton, CustomSecondaryButton } from "../../physician_page/styled";
import styled from "styled-components";

const { Title, Text } = Typography;
const { Option } = Select;

const ScriptWrapper = styled.div`
  .ant-select-single:not(.ant-select-customize-input) .ant-select-selector {
    height: 40px;
    background-color: var(--primary-bg) !important;
    border: 1px solid var(--border-color) !important;
  }
  .ant-select-selector {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .ant-select {
    background-color: var(--primary-bg) !important;
    color: var(--primary-text-color);
    font-size: 16px;
  }
  .ant-select-arrow {
    color: var(--primary-text-color);
  }
  .ant-select-item {
    background-color: var(--primary-bg) !important;
    color: var(--primary-text-color);
  }
`;

const ScriptEditor = ({ scriptToEdit, onSaveSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [savingScript, setSavingScript] = useState(false);
  const [generatingScript, setGeneratingScript] = useState(false);
  const [boardMembers, setBoardMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openScriptAiModel, setOpenScriptAiModel] = useState(false);

  const editMode = !!scriptToEdit && !Array.isArray(scriptToEdit);

  useEffect(() => {
    fetchPhysicians();
    if (editMode) {
      form.setFieldsValue({
        title: scriptToEdit.scriptTitle,
        content: scriptToEdit.scriptContent,
        boardMembers: scriptToEdit.boardMember.id || [],
      });
    } else {
      form.resetFields();
    }
  }, [scriptToEdit, form]);

  const fetchPhysicians = async () => {
    try {
      setLoading(true);
      const response = await physicianService.getAll(0,1,"","","");
      const boardMembersList = (response?.data?.results || []).filter(
        (physician) => physician.boardMember === true
      );
      setBoardMembers(boardMembersList);
    } catch (error) {
      notify('error', 'Failed to load physicians');
    } finally {
      setLoading(false);
    }
  };

  const assignScriptToBoardMember = async (boardMemberIds, scriptId) => {
    try {
      if (!boardMemberIds || boardMemberIds.length === 0) return;
      const normalizedBoardMemberId = Array.isArray(boardMemberIds)
      ? boardMemberIds.flat()[0]
      : boardMemberIds;

      const response = await scriptService.assignBoardMember({
        boardMember: normalizedBoardMemberId,
        scriptId: [scriptId],
      });
      return response;
    } catch (error) {
      notify('error', 'Failed to assign board members to script');
    }
  };
  
  const handleSaveScript = async (values) => {
    setSavingScript(true);
    try {
      let scriptId;
      if (editMode) {
        await scriptService.updateScript(
          scriptToEdit.id,
          { scriptTitle: values.title, scriptContent: values.content }
        );
        scriptId = scriptToEdit.id;
        notify('success', 'Script updated successfully!');
      } else {
        const createResponse = await scriptService.createScript({
          scriptTitle: values.title,
          scriptContent: values.content,
        });
        scriptId = createResponse?.data?.script?.id;
        notify('success', 'Script saved successfully!');
      }

      if (scriptId && values.boardMembers && values.boardMembers.length > 0) {
        await assignScriptToBoardMember(values.boardMembers, scriptId);
      }

      onSaveSuccess();
    } catch (error) {
      notify('error', 'Failed to save script. Please try again.');
    } finally {
      setSavingScript(false);
    }
  };

  const handleGenerateScript = () => { /* Placeholder */ };
  
  const AiScriptUse = (aiResponse) => {
    form.setFieldsValue({
      title: aiResponse?.title,
      content: aiResponse?.description,
    });
  };

  return (
    <>
      {openScriptAiModel && (
        <GenereateScriptWithAi
          AiScriptUse={AiScriptUse}
          open={openScriptAiModel}
          setOpen={setOpenScriptAiModel}
        />
      )}
      <ScriptWrapper>
      <Card
        bordered={false}
        style={{
          padding: "10px",
          borderRadius: "10px",
          border: "1px solid var(--border-color)",
          boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
          backgroundColor: "var(--primary-bg)",
          margin: "10px 64px"
        }}
      >
        <Row gutter={[0, 24]}>
          <Col span={24}>
            <Row justify="space-between" align="top">
              <Col>
                <Space direction="vertical" size={0}>
                  <Title
                    level={4}
                    style={{ margin: 0, color: "var(--primary-text-color)" }}
                  >
                    <EditOutlined style={{ marginRight: "12px" }} />
                    {editMode ? "Edit Script" : "Create New Script"}
                  </Title>
                  <Text
                    type="secondary"
                    style={{
                      color: "var(--secondary-text-color)",
                      marginLeft: "28px",
                    }}
                  >
                    {editMode
                      ? "Make changes to your script"
                      : "Create and save your video script"}
                  </Text>
                </Space>
              </Col>
              <Col>
                <Space>
                  <CustomButton
                    type="primary"
                    onClick={() => setOpenScriptAiModel(true)}
                    icon={<BsStars />}
                  >
                    Generate with AI
                  </CustomButton>
                  <CustomSecondaryButton
                    type="text"
                    shape="circle"
                    // icon={<CloseOutlined />}
                    onClick={onCancel}
                    style={{ color: "var(--primary-text-color)" }}
                  >
                     <CloseOutlined />
                  </CustomSecondaryButton>
                </Space>
              </Col>
            </Row>
          </Col>

          <Col span={24}>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSaveScript}
              initialValues={{
                title: "",
                content: "",
                boardMembers: [],
              }}
            >
              <Form.Item
                name="title"
                label={
                  <Text style={{ color: "var(--primary-text-color)" }}>
                    Script Title
                  </Text>
                }
                rules={[
                  {
                    required: true,
                    message: "Please enter a title for your script",
                  },
                ]}
              >
                <Input
                  placeholder="e.g., Introduction to new drug"
                  size="large"
                  style={{
                    backgroundColor: "var(--field-bg, #f5f5f5)",
                    color: "var(--primary-text-color)",
                    border: "1px solid var(--border-color)",
                  }}
                />
              </Form.Item>
              <Form.Item
                name="content"
                label={
                  <Text style={{ color: "var(--primary-text-color)" }}>
                    Script Content
                  </Text>
                }
                rules={[
                  { required: true, message: "Please enter your script content" },
                ]}
              >
                <TextArea
                  placeholder="Start writing your script here..."
                  autoSize={{ minRows: 12, maxRows: 12 }}
                  style={{
                    fontSize: "15px",
                    lineHeight: 1.7,
                    padding: "16px",
                    fontFamily: "'SF Mono', 'Courier New', Courier, monospace",
                    backgroundColor: "var(--field-bg, #f5f5f5)",
                    color: "var(--primary-text-color)",
                    border: "1px solid var(--border-color)",
                  }}
                />
              </Form.Item>
              <Form.Item
                label={
                  <Space>
                    <TeamOutlined />
                    <Text style={{ color: "var(--primary-text-color)" }}>
                      Assign to Board Members
                    </Text>
                  </Space>
                }
                name="boardMembers"
              >
                <Select
                  placeholder="Select board members to review and approve"
                  showSearch
                  loading={loading}
                  tagRender={(props) => {
                    const { label, closable, onClose } = props;
                    return (
                      <Tag
                        color="#8a3ef4"
                        closable={closable}
                        onClose={onClose}
                        style={{ marginRight: "3px" }}
                      >
                        {label}
                      </Tag>
                    );
                  }}
                  style={{
                    backgroundColor: "var(--primary-bg)",
                    color: "var(--primary-text-color)",
                  }}
                  filterOption={(input, option) =>
                    option?.label.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {boardMembers.map((physician) => (
                    <Option
                      key={physician.id}
                      label={`${physician.name} : ${physician?.specialty} - ${physician?.email}`}
                      value={physician.id}
                      style={{borderBottom: "1px solid var(--border-color)"}}
                    >
                      {physician.name} : {physician?.specialty} - {physician?.email}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item style={{ marginBottom: 0, marginTop: "24px" }}>
                <Row justify="end" align="middle">
                  <Col>
                    <Space>
                      <CustomSecondaryButton
                        type="default"
                        onClick={() => form.resetFields()}
                        style={{
                          backgroundColor: "var(--primary-bg)",
                          color: "var(--primary-text-color)",
                          border: "1px solid var(--border-color)",
                        }}
                      >
                        Clear
                      </CustomSecondaryButton>
                      <CustomButton
                        type="primary"
                        htmlType="submit"
                        icon={<SaveOutlined />}
                        loading={savingScript}
                        style={{
                          backgroundColor: "#8a3ef4",
                          color: "var(--primary-text-color)",
                          borderColor: "#8a3ef4",
                        }}
                      >
                        {editMode ? "Update Script" : "Save Script"}
                      </CustomButton>
                    </Space>
                  </Col>
                </Row>
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </Card>
      </ScriptWrapper>
    </>
  );
};

export default ScriptEditor; 